import React from 'react';
import { Button } from './ui';
import { YourReportsBanner } from './YourReportsBanner';

interface Props {
    onStart: () => void;
    onSignIn: () => void;
    isAuthenticated: boolean;
    onNavigate?: (page: string) => void;
    onOpenEstate?: (id: string) => void;
    onNewCase?: () => void;
}

const STEPS: Array<{ title: string; body: string }> = [
    {
        title: 'Enter the estate',
        body: 'Property, accounts, pensions, gifts, foreign assets, debts. Plain forms — no jargon, no marketing.',
    },
    {
        title: 'We read it carefully',
        body: 'Allowances, transfer of unused NRB, residence nil-rate band, taper on lifetime gifts, spouse exemption — applied per the Act.',
    },
    {
        title: 'A written opinion',
        body: 'Probate route, HMRC filing form, inheritance tax exposure, deadlines. Citations to ss.IHTA 1984 and HMRC manuals throughout.',
    },
];

export const HomePage: React.FC<Props> = ({
    onStart,
    onSignIn,
    isAuthenticated,
    onNavigate,
    onOpenEstate,
    onNewCase,
}) => {
    const handleCta = () => {
        if (isAuthenticated) onStart();
        else onSignIn();
    };

    return (
        <main className="min-h-screen bg-paper px-6 pt-12 pb-24">
            <div className="max-w-[820px] mx-auto">
                {/* Authenticated users see their saved cases above the hero */}
                {isAuthenticated && onOpenEstate && onNewCase && (
                    <div className="mb-12">
                        <YourReportsBanner
                            onOpenEstate={onOpenEstate}
                            onNewCase={onNewCase}
                        />
                    </div>
                )}

                {/* Editorial hero */}
                <section className="mb-14">
                    <div className="text-[10px] tracking-eyebrow uppercase text-accent font-semibold mb-6">
                        For executors · England &amp; Wales
                    </div>
                    <h1 className="font-serif text-[44px] leading-[1.05] tracking-[-0.02em] text-ink max-w-[680px]">
                        An unhurried audit of the estate, before you file anything with HMRC.
                    </h1>
                    <p className="font-serif italic text-[17px] text-ink2 mt-6 max-w-[560px] leading-relaxed">
                        We read the estate line by line — property, pensions, gifts, foreign assets —
                        and produce a written opinion on probate route, filing obligations, and
                        inheritance tax exposure. Plain language. Citations to the Act and HMRC
                        manuals throughout.
                    </p>

                    <div className="flex items-center gap-8 mt-10">
                        <Button variant="primary" iconTrailing="→" onClick={handleCta}>
                            Begin a review
                        </Button>
                        <a
                            href="#example"
                            className="text-[12px] text-ink3 hover:text-ink underline decoration-rule underline-offset-4"
                        >
                            See an example report
                        </a>
                    </div>
                </section>

                {/* Three-step prospectus */}
                <section
                    id="how"
                    className="grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-rule pt-10"
                >
                    {STEPS.map((s, i) => (
                        <article key={s.title}>
                            <div className="font-serif italic text-[14px] text-accent mb-2">
                                {['I', 'II', 'III'][i]}.
                            </div>
                            <h3 className="font-serif text-[17px] text-ink mb-2">{s.title}</h3>
                            <p className="text-[13px] text-ink2 leading-relaxed">{s.body}</p>
                        </article>
                    ))}
                </section>

                {/* Footer — keeps Privacy / Terms callbacks intact */}
                <footer className="mt-20 pt-6 border-t border-rule flex flex-wrap items-baseline justify-between gap-4 text-[11px] text-ink3">
                    <div>
                        Estate<span className="italic font-serif">clarity</span> · Probate &amp; IHT review
                    </div>
                    <div className="flex gap-6">
                        <a
                            href="mailto:support@estateclarity.co.uk"
                            className="hover:text-ink"
                        >
                            Contact
                        </a>
                        <button
                            type="button"
                            onClick={() => onNavigate?.('privacy')}
                            className="hover:text-ink"
                        >
                            Privacy
                        </button>
                        <button
                            type="button"
                            onClick={() => onNavigate?.('terms')}
                            className="hover:text-ink"
                        >
                            Terms
                        </button>
                    </div>
                </footer>
            </div>
        </main>
    );
};
